// Phase 8 + 8.1 — AuthContext + AuthProvider + useAuth.
//
// Session-cookie auth. Every axios call goes through `api`
// (`withCredentials: true`), so the browser attaches
// `session_token` / `procurement_session` automatically.
//
// State machine:
//   user === null  → checking (mount fetch of /auth/me in flight)
//   user === false → definitely anonymous
//   user === object → authenticated
//
// Phase 8.1 race-condition fix (per Emergent Auth playbook):
// when the URL fragment contains `session_id=`, we're mid-callback.
// SKIP the initial `/auth/me` call — otherwise it 401s (no cookie
// yet), sets user=false, and redirects to /login BEFORE AuthCallback
// can exchange the token.

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, detailFromError } from "@/lib/api";

const AuthContext = createContext(null);

function isMidOAuthCallback() {
  try {
    return typeof window !== "undefined"
      && window.location?.hash?.includes("session_id=");
  } catch (_e) {
    return false;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (_e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will call refresh() itself after the exchange.
    if (isMidOAuthCallback()) {
      setUser(null);  // stay in "checking" so ProtectedRoute renders the loader
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      // best-effort; the cookie may already be dead
    }
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh, detailFromError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}
